import { Button, Tag, Popconfirm } from 'antd'
import { RedoOutlined, SearchOutlined } from '@ant-design/icons'

import FilterDropdown from '$components/FilterDropdown'
import TextHighlighter from '$components/TextHighlighter'
import Tooltip from '$components/Tooltip'
import TruncateText from '$components/TruncateText'
import { currencyRenderer, dateWithTimeRenderer } from '$utils/i18n'

import ExpirationDate from './ExpirationDate'
import RequestRejectedCell from './RequestRejectedCell'
import { calculateOrderPartPriceRange, summarizeDeliveryTimes } from './utils'
import { StyledLink } from './ExpandedRow/styledComponents'

export const getColumns = ({
  t,
  filters,
  theme,
  handleAcceptOffer,
  openOrderService,
  orderPartFormData,
  orderPartListsData,
  orderPartListStatus,
}) => [
  {
    title: (
      <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
        {t('my_orders.order_nr')}
      </div>
    ),
    dataIndex: 'order_number',
    sorter: true,
    filterDropdown: (props) => (
      <FilterDropdown
        {...props}
        operators={['contains']}
        parseValue={(value) => value}
      />
    ),
    filterIcon: (filtered) => (
      <Tooltip title={t('my_orders.search_icon_tooltip_request_no')}>
        <SearchOutlined
          style={{ color: filtered ? '#1890ff' : theme.colorPrimary }}
        />
      </Tooltip>
    ),
    render: (ordernr) => {
      return (
        <div style={{ minWidth: '100px' }}>
          <b>
            <TextHighlighter
              text={ordernr ? ordernr.toString() : ''}
              filters={
                filters.order_number ? [{ value: filters.order_number }] : []
              }
            />
          </b>
        </div>
      )
    },
  },
  {
    title: (
      <div style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
        {t('my_orders.order_date')}
      </div>
    ),
    dataIndex: 'time_stamp',
    sorter: true,
    defaultSortOrder: 'descend',
    render: (_value, record) =>
      dateWithTimeRenderer(t)(record.list_of_orderparts[0].time_stamp),
  },
  {
    title: t('my_orders.date_of_expiration'),
    dataIndex: 'date_of_expiration', // Unique, unused key for sorting
    sorter: true,
    render: (_value, record) => {
      if (!record.provider_accepted_offer) {
        return ''
      }

      return <ExpirationDate date_of_expiration={record.date_of_expiration} />
    },
  },
  {
    title: t('my_orders.parts_amount'),
    dataIndex: 'list_of_orderparts',
    sorter: true,
    render: (_value, record) => {
      const partsAmount = record.list_of_orderparts.filter(
        (orderPart) =>
          !orderPart.rejected_by_client &&
          orderPart.status_info !== 'discarded',
      ).length
      return partsAmount
    },
  },
  {
    title: t('my_orders.materials'),
    dataIndex: 'materials', // Unique, unused key for sorting
    sorter: true,
    render: (_value, record) => {
      const materialNames = record.list_of_orderparts
        .filter(
          (orderPart) =>
            !orderPart.rejected_by_client &&
            orderPart.status_info !== 'discarded',
        )
        .reduce((acc, obj) => {
          if (obj.operator_additive_material?.material_name) {
            acc.add(obj.operator_additive_material.material_name)
          }
          return acc
        }, new Set())

      const materialNamesString =
        Array.from(materialNames).join(', ') || t('unmapped')

      return (
        <Tooltip title={materialNamesString}>
          <TruncateText text={materialNamesString} />
        </Tooltip>
      )
    },
    ellipsis: true,
  },
  {
    title: t('my_orders.delivery_time'),
    dataIndex: 'delivery_time', // Unique, unused key for sorting
    sorter: true,
    render: (_value, record) => {
      if (!record.provider_accepted_offer) {
        return ''
      }

      if (record.client_accepted_offer) {
        const selectedOffers = record.list_of_orderparts
          .filter(
            (orderPart) =>
              !orderPart.rejected_by_client &&
              orderPart.status_info !== 'discarded',
          )
          .flatMap((orderPart) => orderPart.provider_offers)
          .filter((offer) => offer.selected === true)

        if (selectedOffers.length > 0) {
          const maxDeliveryDays = Math.max(
            ...selectedOffers.map((offer) => offer.delivery_days),
          )
          return maxDeliveryDays + ' ' + t('days')
        }
      }

      let offers_delivery_days
      if (orderPartFormData[record.id]) {
        offers_delivery_days = Object.values(orderPartFormData[record.id])
          .filter(
            (orderPart) =>
              !orderPart.rejected_by_client &&
              orderPart.status_info !== 'discarded',
          )
          .map((orderPart) =>
            Object.values(orderPart.provider_offers || {}).map(
              (offer) => offer.delivery_days || offer.base_production_time,
            ),
          )
          .flat()
          .filter((days) => days)
      } else {
        offers_delivery_days = record.list_of_orderparts
          .filter(
            (orderPart) =>
              !orderPart.rejected_by_client &&
              orderPart.status_info !== 'discarded',
          )
          .map(
            (orderPart) =>
              orderPart.provider_offers?.map(
                (offer) => offer.delivery_days || offer.base_production_time,
              ) || [],
          )
          .flat()
          .filter((days) => days)
      }

      return offers_delivery_days.length > 0
        ? t(
            'received_requests.days',
            summarizeDeliveryTimes(offers_delivery_days),
          )
        : ''
    },
  },
  {
    title: t('my_orders.price_total'),
    dataIndex: 'price_total', // Unique, unused key for sorting
    render: (_value, record) => {
      if (!record.provider_accepted_offer) {
        return ''
      }

      if (record.client_accepted_offer) {
        const selectedOffers = record.list_of_orderparts
          .filter(
            (orderPart) =>
              !orderPart.rejected_by_client &&
              orderPart.status_info !== 'discarded',
          )
          .flatMap((orderPart) => orderPart.provider_offers)
          .filter((offer) => offer.selected === true)

        if (selectedOffers.length > 0) {
          const totalPrice = selectedOffers.reduce(
            (sum, offer) => sum + offer.base_price * offer.amount,
            0,
          )
          return currencyRenderer(t)(totalPrice)
        }
      }

      let priceRanges
      if (orderPartFormData[record.id]) {
        priceRanges = Object.values(orderPartFormData[record.id])
          .filter(
            (orderPart) =>
              !orderPart.rejected_by_client &&
              orderPart.status_info !== 'discarded',
          )
          .map((orderPart) =>
            calculateOrderPartPriceRange(
              Object.values(orderPart.provider_offers),
            ),
          )
      } else {
        priceRanges = record.list_of_orderparts
          .filter(
            (orderPart) =>
              !orderPart.rejected_by_client &&
              orderPart.status_info !== 'discarded',
          )
          .map((orderPart) =>
            calculateOrderPartPriceRange(orderPart.provider_offers),
          )
      }

      const totalMin = priceRanges.reduce((sum, range) => sum + range.min, 0)
      const totalMax = priceRanges.reduce((sum, range) => sum + range.max, 0)

      if (totalMin === totalMax) {
        return currencyRenderer(t)(totalMin)
      }

      return `${currencyRenderer(t)(totalMin)} - ${currencyRenderer(t)(totalMax)}`
    },
  },
  {
    title: 'Status',
    width: 270,
    onCell: () => ({ style: { display: 'inline-block' } }),
    render: (record) => {
      if (record.client_accepted_offer) {
        return (
          <Tooltip title={t('service.order_part.tooltip.offer_accepted')}>
            <Tag color="success">
              <TruncateText
                text={t('service.order_part.status.offer_accepted')}
              />
            </Tag>
          </Tooltip>
        )
      }

      if (
        record.provider_accepted_offer === false &&
        record.client_accepted_offer === false
      ) {
        return (
          <Tooltip title={t('service.order_part.tooltip.offer_discarded')}>
            <Tag color="error">
              <TruncateText
                text={t('service.order_part.status.offer_discarded')}
              />
            </Tag>
          </Tooltip>
        )
      }

      if (record.provider_accepted_offer === true) {
        const orderPartList = orderPartListsData.find(
          (orderPartList) => orderPartList.id === record.id,
        )
        const allReviewed = orderPartList.list_of_orderparts
          .filter(
            (orderPart) =>
              !orderPart.rejected_by_client &&
              orderPart.status_info !== 'discarded',
          )
          .every((orderPart) =>
            orderPartListStatus[record.id]?.includes(orderPart.id),
          )

        const isExpired = orderPartList.list_of_orderparts
          .map(({ status_info }) => status_info)
          .includes('expired')

        return (
          <Tooltip
            title={
              isExpired
                ? t('service.order_part.tooltip.offer_expired')
                : !allReviewed
                  ? t('service.order_part.tooltip.please_review')
                  : t('service.order_part.tooltip.accept_and_order')
            }
          >
            <Button
              type="primary"
              size="small"
              disabled={!allReviewed || isExpired}
              onClick={() => handleAcceptOffer(record.id)}
            >
              <TruncateText text={t('my_orders.accept_and_order')} />
            </Button>
          </Tooltip>
        )
      }

      if (record.provider_accepted_offer === false) {
        return (
          <RequestRejectedCell
            t={t}
            reason={record.provider_rejection_reason}
          />
        )
      }

      if (
        record.client_accepted_offer === null &&
        record.provider_accepted_offer === null &&
        record.service_provider_company !== null
      ) {
        return (
          <Tooltip title={t('service.order_part.tooltip.request_forwarded')}>
            <Tag color="cyan" style={{ width: 'fit-content' }}>
              <TruncateText
                text={t('service.order_part.status.request_forwarded')}
              />
            </Tag>
          </Tooltip>
        )
      }

      return (
        <Tooltip title={t('service.order_part.tooltip.offer_pending')}>
          <Tag color="warning">
            <TruncateText text={t('service.order_part.status.offer_pending')} />
          </Tag>
        </Tooltip>
      )
    },
    ellipsis: true,
  },
  {
    title: '',
    width: 100,
    render: (record) => {
      return (
        <div style={{ width: 'fit-content' }}>
          <Tooltip title={t('my_orders.repeat_order')}>
            <Popconfirm
              placement="bottomRight"
              title={t('my_orders.repeat_order_popconfirm_title')}
              description={t('my_orders.repeat_order_popconfirm_description')}
              onConfirm={(event) => {
                openOrderService(record)
                event.stopPropagation()
              }}
              onCancel={(event) => event.stopPropagation()}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <StyledLink onClick={(event) => event.stopPropagation()}>
                <RedoOutlined style={{ fontSize: '16px' }} />
              </StyledLink>
            </Popconfirm>
          </Tooltip>
        </div>
      )
    },
  },
]
